import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StanButton } from '@/components';
import IcCopy from '@/assets/icons/ic-copy.svg';
import { copyToClipboard } from '@/lib/helpers';
import { getMFADescription, getMFATitle, MFAConfigSource } from '../../../constants/mfa';
import { MFA } from '@/lib';

interface MFAAuthenticatorSetupProps {
    source: MFAConfigSource;
    totpURI: string;
    secret: string;
    showSetupKey: boolean;
    onToggleView: () => void;
    onVerifyCode: (code: string) => Promise<boolean>;
    isLoading?: boolean;
    error?: string | null;
    onClearError?: () => void;
}

export const MFAAuthenticatorSetup: React.FC<MFAAuthenticatorSetupProps> = ({
    source,
    totpURI,
    secret,
    showSetupKey,
    onToggleView,
    onVerifyCode,
    isLoading = false,
    error,
    onClearError,
}) => {
    const [otpDigits, setOtpDigits] = useState<string[]>(Array(MFA.OTP_LENGTH).fill(''));
    const otpInputRefs = useRef<HTMLInputElement[]>([]);

    const title = getMFATitle('authenticator_app', source, showSetupKey);
    const description = getMFADescription('authenticator_app', source, showSetupKey);

    // Generate QR code URL using external API
    const qrCodeUrl = useMemo(() => {
        if (!totpURI) return '';
        return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(totpURI)}&size=155x155`;
    }, [totpURI]);

    // Reset OTP digits when error occurs
    useEffect(() => {
        if (error) {
            setOtpDigits(Array(MFA.OTP_LENGTH).fill(''));
        }
    }, [error]);

    const handleCopySetupKey = () => {
        copyToClipboard(secret, {
            successMessage: 'Setup key copied!',
            showNotifications: true,
        });
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, idx = 0) => {
        e.preventDefault();
        const numericValue = e.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .slice(0, MFA.OTP_LENGTH - idx);

        if (!numericValue) return;

        const code = [...otpDigits];
        for (let i = 0; i < numericValue.length; i++) {
            code[idx + i] = numericValue[i] ?? '';
        }

        setOtpDigits(code);

        // Auto-submit if all digits filled
        if (code.every(ch => ch && ch.length === 1) && code.length === MFA.OTP_LENGTH) {
            onVerifyCode(code.join(''));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const value = e.target.value.replace(/\D/g, '');
        const newDigits = [...otpDigits];
        newDigits[idx] = value.slice(-1); // Only keep last digit
        setOtpDigits(newDigits);
        onClearError?.();

        if (value && idx < MFA.OTP_LENGTH - 1) {
            const nextInput = otpInputRefs.current[idx + 1];
            if (nextInput) {
                nextInput.focus();
                nextInput.select();
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
        if (e.key === 'Backspace') {
            if (otpDigits[idx]?.length === 0 && idx > 0) {
                const prevInput = otpInputRefs.current[idx - 1];
                if (prevInput) {
                    prevInput.focus();
                    prevInput.select();
                }
            } else {
                setOtpDigits(prev => {
                    const newDigits = [...prev];
                    newDigits[idx] = '';
                    return newDigits;
                });
            }
            return;
        }
        if (e.key === 'ArrowLeft' && idx > 0) {
            const prevInput = otpInputRefs.current[idx - 1];
            if (prevInput) {
                prevInput.focus();
                prevInput.select();
            }
            return;
        }
        if (e.key === 'ArrowRight' && idx < otpDigits.length - 1) {
            const nextInput = otpInputRefs.current[idx + 1];
            if (nextInput) {
                nextInput.focus();
                nextInput.select();
            }
            return;
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const code = otpDigits.join('');
        if (code.length === MFA.OTP_LENGTH) {
            onVerifyCode(code);
        }
    };

    const otpFilled = useMemo(() => otpDigits.every(digit => digit !== ''), [otpDigits]);

    return (
        <div className='flex flex-col items-center text-center'>
            {/* Header */}
            <div className='mb-4 flex flex-col items-center'>
                <h3 className='text-heading-h3 mb-2'>{title}</h3>
                <p className='text-gray text-body-sm max-w-sm'>{description}</p>
            </div>

            {/* QR Code or Setup Key */}
            {!showSetupKey ? (
                <div className='mb-6'>
                    <div className='mb-6 flex justify-center'>
                        {qrCodeUrl && <img src={qrCodeUrl} alt='QR Code for authenticator app' className='size-40' />}
                    </div>
                    <div>
                        <p className='text-gray text-body-sm'>Can&apos;t scan the code?</p>
                        <button
                            className='text-brand-main text-body-sm w-full cursor-pointer font-semibold'
                            onClick={onToggleView}
                        >
                            View the setup key instead
                        </button>
                    </div>
                </div>
            ) : (
                <div className='mb-6 w-full'>
                    <div className='bg-neutral-subtle mb-4 rounded-lg p-4'>
                        <p className='text-brand-main text-xl font-semibold break-all'>{secret}</p>
                    </div>
                    <StanButton
                        name='mfa-copy-setup-key'
                        variant='outline'
                        size='md'
                        className='mb-3 w-full'
                        onClick={handleCopySetupKey}
                        icon={IcCopy}
                    >
                        Copy Setup Key
                    </StanButton>
                    <button
                        className='text-body-sm text-brand-main mt-2 w-fit cursor-pointer font-semibold'
                        onClick={onToggleView}
                    >
                        Scan QR code instead
                    </button>
                </div>
            )}

            {/* OTP Input */}
            <p className='text-gray text-body-sm mb-3'>2. Enter the one-time code from your authenticator</p>

            <form onSubmit={handleSubmit} className='w-full'>
                <div className='mb-4 flex justify-center gap-2'>
                    {Array.from({ length: MFA.OTP_LENGTH }).map((_, index) => (
                        <input
                            key={index}
                            value={otpDigits[index]}
                            ref={el => {
                                if (el) {
                                    otpInputRefs.current[index] = el;
                                }
                            }}
                            type='text'
                            pattern='[0-9]*'
                            inputMode='numeric'
                            maxLength={1}
                            onChange={e => handleChange(e, index)}
                            onKeyDown={e => handleKeyDown(e, index)}
                            onPaste={e => handlePaste(e, index)}
                            placeholder=''
                            className={`${error ? 'border-negative' : 'border-brand-soft'} text-brand-main h-11 w-8 rounded-md border-2 px-0 py-0.5 text-center text-2xl font-semibold transition duration-300 outline-none md:h-16 md:w-14 md:p-0 md:text-4xl`}
                        />
                    ))}
                </div>

                {error && (
                    <div className='mb-4 w-full text-center'>
                        <span className='text-negative text-body-sm font-semibold'>{error}</span>
                    </div>
                )}

                <StanButton
                    name='mfa-verify-button'
                    type='submit'
                    className='w-full'
                    size='md'
                    disabled={!otpFilled || isLoading}
                    loading={isLoading}
                >
                    Continue
                </StanButton>
            </form>
        </div>
    );
};
